<template>
  <canvas ref="canvasEl" class="voice-waveform" />
</template>

<script lang="ts">
import {
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";

const BAR_WIDTH = 2;
const BAR_GAP = 3;
const BAR_MIN_HEIGHT = 2;
const AMPLITUDE_SCALE = 3.5;

export default defineComponent({
  name: "VoiceWaveform",
  props: {
    stream: {
      type: Object as PropType<MediaStream | null>,
      default: null,
    },
  },
  setup(props) {
    const canvasEl = ref<HTMLCanvasElement | null>(null);
    let animationId: number | null = null;
    let analyser: AnalyserNode | null = null;
    let audioCtx: AudioContext | null = null;
    let resizeObserver: ResizeObserver | null = null;

    function syncSize(canvas: HTMLCanvasElement) {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const physW = Math.round(w * dpr);
      const physH = Math.round(h * dpr);
      if (canvas.width !== physW || canvas.height !== physH) {
        canvas.width = physW;
        canvas.height = physH;
      }
    }

    function draw() {
      const canvas = canvasEl.value;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      syncSize(canvas);

      const dpr = window.devicePixelRatio || 1;
      const W = canvas.clientWidth;
      const H = canvas.clientHeight;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      const bufferLength = analyser ? analyser.frequencyBinCount : 0;
      const dataArray = new Uint8Array(bufferLength);
      if (analyser) analyser.getByteTimeDomainData(dataArray);

      const totalBars = Math.floor((W + BAR_GAP) / (BAR_WIDTH + BAR_GAP));
      const totalWidth = totalBars * (BAR_WIDTH + BAR_GAP) - BAR_GAP;
      const offsetX = (W - totalWidth) / 2;

      const color = getComputedStyle(canvas)
        .getPropertyValue("--waveform-bar-color")
        .trim();

      ctx.fillStyle = color || "rgba(160, 160, 160, 0.75)";

      for (let i = 0; i < totalBars; i++) {
        const idx = bufferLength
          ? Math.floor((i / totalBars) * bufferLength)
          : 0;
        const amplitude = bufferLength
          ? Math.abs((dataArray[idx] ?? 128) / 128.0 - 1.0)
          : 0;
        const barH = Math.max(BAR_MIN_HEIGHT, amplitude * H * AMPLITUDE_SCALE);
        const x = offsetX + i * (BAR_WIDTH + BAR_GAP);
        const y = (H - barH) / 2;
        const r = BAR_WIDTH / 2;

        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + BAR_WIDTH - r, y);
        ctx.arcTo(x + BAR_WIDTH, y, x + BAR_WIDTH, y + r, r);
        ctx.lineTo(x + BAR_WIDTH, y + barH - r);
        ctx.arcTo(x + BAR_WIDTH, y + barH, x + BAR_WIDTH - r, y + barH, r);
        ctx.lineTo(x + r, y + barH);
        ctx.arcTo(x, y + barH, x, y + barH - r, r);
        ctx.lineTo(x, y + r);
        ctx.arcTo(x, y, x + r, y, r);
        ctx.closePath();
        ctx.fill();
      }

      animationId = requestAnimationFrame(draw);
    }

    function startVisualization(stream: MediaStream) {
      audioCtx = new AudioContext();
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;

      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);

      const canvas = canvasEl.value;
      if (canvas) syncSize(canvas);

      draw();
    }

    function stopVisualization() {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
      analyser?.disconnect();
      analyser = null;
      audioCtx?.close().catch(() => {});
      audioCtx = null;

      const canvas = canvasEl.value;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;
        ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx?.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
      }
    }

    watch(
      () => props.stream,
      (stream) => {
        stopVisualization();
        if (stream) startVisualization(stream);
      }
    );

    onMounted(() => {
      const canvas = canvasEl.value;
      if (!canvas) return;

      resizeObserver = new ResizeObserver(() => syncSize(canvas));
      resizeObserver.observe(canvas);

      // Stream may already be set when this component mounts (v-if flips after
      // recording starts), so kick off visualization here if needed.
      if (props.stream) startVisualization(props.stream);
    });

    onBeforeUnmount(() => {
      stopVisualization();
      resizeObserver?.disconnect();
    });

    return { canvasEl };
  },
});
</script>

<style scoped lang="scss">
.voice-waveform {
  display: block;
  width: 100%;
  height: 28px;
  flex: 1;
  min-width: 0;

  @include themed() {
    --waveform-bar-color: #{t($grey-50)};
  }
}
</style>
